import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

// Submitted to Netlify Forms. Netlify finds the form at deploy time from the
// hidden copy in public/index.html (its name and field names must match this
// one) and emails each submission to the address set in the Netlify dashboard,
// so no address appears on the site.
//
// Link here with /?topic=<words>#contact (see contactLink) to say what the
// message is about: it shows above the fields and becomes the email subject.
const FORM_NAME = 'contact';
const EMPTY = { name: '', email: '', message: '', 'bot-field': '' };
const MAX_FILE_BYTES = 8 * 1024 * 1024; // Netlify's limit for a whole submission

export const contactLink = (topic) => `/?topic=${encodeURIComponent(topic)}#contact`;

function ContactForm() {
  const { search } = useLocation();
  const topic = (new URLSearchParams(search).get('topic') || '').slice(0, 80);
  const [fields, setFields] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error | too-big

  const onChange = (e) => setFields({ ...fields, [e.target.name]: e.target.value });

  const onFile = (e) => {
    const picked = e.target.files[0] || null;
    setFile(picked);
    setStatus(picked && picked.size > MAX_FILE_BYTES ? 'too-big' : 'idle');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === 'too-big') return;
    setStatus('sending');
    const body = new FormData();
    body.append('form-name', FORM_NAME);
    body.append('subject', topic ? `Oneshoo contact: ${topic}` : 'Oneshoo contact');
    Object.keys(fields).forEach((k) => body.append(k, fields[k]));
    if (file) body.append('attachment', file);
    try {
      // No Content-Type header: the browser sets the multipart boundary.
      const res = await fetch('/', { method: 'POST', body });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setFields(EMPTY);
      setFile(null);
      setStatus('sent');
    } catch (err) {
      setStatus('error');
    }
  };

  if (status === 'sent') {
    return (
      <div className="contact-sent" role="status">
        <strong>Thanks, your message is on its way.</strong>
        <span>We&apos;ll reply to the email address you gave us.</span>
        <button type="button" className="btn btn-ghost" onClick={() => setStatus('idle')}>
          Send another
        </button>
      </div>
    );
  }

  return (
    <form
      className="contact-form"
      name={FORM_NAME}
      method="POST"
      encType="multipart/form-data"
      data-netlify="true"
      netlify-honeypot="bot-field"
      onSubmit={onSubmit}
    >
      <input type="hidden" name="form-name" value={FORM_NAME} />
      <p className="contact-honeypot" aria-hidden="true">
        <label>
          Leave this empty
          <input name="bot-field" tabIndex={-1} autoComplete="off" value={fields['bot-field']} onChange={onChange} />
        </label>
      </p>
      {topic && (
        <p className="contact-topic">
          About: <strong>{topic}</strong>
        </p>
      )}
      <div className="contact-row">
        <label className="contact-field">
          <span>Name</span>
          <input name="name" autoComplete="name" required value={fields.name} onChange={onChange} />
        </label>
        <label className="contact-field">
          <span>Email</span>
          <input type="email" name="email" autoComplete="email" required value={fields.email} onChange={onChange} />
        </label>
      </div>
      <label className="contact-field">
        <span>Message</span>
        <textarea name="message" rows={5} required value={fields.message} onChange={onChange} />
      </label>
      <label className="contact-field">
        <span>Attachment (optional): a log file or screenshot, up to 8 MB</span>
        <input type="file" name="attachment" onChange={onFile} />
      </label>
      {status === 'too-big' && (
        <p className="contact-error" role="alert">
          That file is over 8 MB. Please pick a smaller one.
        </p>
      )}
      {status === 'error' && (
        <p className="contact-error" role="alert">
          Sorry, that didn&apos;t send. Please try again in a moment.
        </p>
      )}
      <button type="submit" className="btn btn-primary" disabled={status === 'sending' || status === 'too-big'}>
        {status === 'sending' ? 'Sending…' : 'Send message'}
      </button>
    </form>
  );
}

export default ContactForm;
